import {
  DateRangeViewSetting,
  TimelineGridPrecisionOptionValue,
} from '@ardoq/api-types';
import {
  TimelineViewRenderingData,
  TimelineViewModelState,
  TimelineLabelWithRenderingParameters,
} from '../types';
import { DEFAULT_TIMELINE_CHART_AND_ITEMS_RENDERING_DATA } from './consts';
import {
  areTwoDateRangesEffectivelyEqual,
  readTimelineDomainRangeViewSetting,
  readTimelineGridPrecisionViewSetting,
} from '../calculations';
import { getTimelineChartRenderingData } from './chartAndItemsRenderingData';
import { getFilteredRenderingDataWithInteractiveNodeStates } from './getFilteredRenderingDataWithInteractiveNodeStates';
import {
  getLabelsWithRenderingParameters,
  getPhasesWithRenderingParameters,
} from '../items/utils';
import { getPersistedCollapsedStateFromViewSettings } from './utils';

type TransformTimelineViewModelDataToRenderingDataArgs =
  TimelineViewModelState & {
    previousDomainRangeViewSettingsValue?: DateRangeViewSetting;
    previousGridPrecisionViewSettingsValue?: TimelineGridPrecisionOptionValue;
    isDataReset?: boolean;
    shouldReadCollapsedStateFromSettings?: boolean;
    preventChartScaleReset?: boolean;
  };

/**
 * This function transforms items model data and the graph properties into rendering data,
 * by enhancing the model data with rendering parameters, such as dimensions, colors,
 * positions and settings bar menus.
 *
 * The composed result should be ready-to-render data, that can be used by the Timeline component.
 * Node that it only returns properties it is responsible for, and is not bound to a certain
 * state structure.
 *
 * If no previous state provided, it uses initial state.
 */
export const transformTimelineViewModelDataToRenderingData = ({
  previousDomainRangeViewSettingsValue,
  previousGridPrecisionViewSettingsValue,
  viewSettings,
  timelineViewRenderingData = DEFAULT_TIMELINE_CHART_AND_ITEMS_RENDERING_DATA,
  hoveredItemId,
  focusedItemId,
  timelineViewModelData,
  viewModelSourceData: {
    viewRelevantComponentsScopeData,
    viewInterface: {
      features: { isModernizedStyle },
    },
  },
  isDataReset,
  shouldReadCollapsedStateFromSettings,
  preventChartScaleReset,
}: TransformTimelineViewModelDataToRenderingDataArgs): TimelineViewRenderingData => {
  const {
    timelineChartRenderingData: {
      timelineChartProperties: {
        clientRect,
        userDefinedLabelsColumnWidth,
        chartWindow,
      },
    },
    backgroundScrollHeight,
    virtualScrollState,
  } = timelineViewRenderingData;

  const { collapsedGroupIds, collapsedFieldDataIds } =
    shouldReadCollapsedStateFromSettings && viewSettings.persistedCollapsedState
      ? getPersistedCollapsedStateFromViewSettings(viewSettings)
      : timelineViewRenderingData;

  const currentChartWindow = { ...chartWindow };

  const domainRangeViewSetting = readTimelineDomainRangeViewSetting(
    viewSettings.domainRange
  );
  const previousDomainRangeViewSetting = readTimelineDomainRangeViewSetting(
    previousDomainRangeViewSettingsValue
  );
  const gridPrecisionViewSetting = readTimelineGridPrecisionViewSetting(
    viewSettings.gridPrecision
  );
  const previousGridPrecisionViewSetting = readTimelineGridPrecisionViewSetting(
    previousGridPrecisionViewSettingsValue
  );

  const shouldResetScale =
    !preventChartScaleReset &&
    (previousGridPrecisionViewSetting !== gridPrecisionViewSetting ||
      !areTwoDateRangesEffectivelyEqual(
        domainRangeViewSetting,
        previousDomainRangeViewSetting
      ));

  if (shouldResetScale) {
    currentChartWindow.windowScale = 1;
  }

  const timelineChartRenderingData = getTimelineChartRenderingData({
    timelineViewModelData,
    viewSettings,
    clientRect,
    userDefinedLabelsColumnWidth,
    chartWindow: currentChartWindow,
  });

  const {
    timelineChartProperties: { dimensions, phasesScaler, visibleDomain },
  } = timelineChartRenderingData;

  // Separate phases and labels updates, as they don't have the same dependencies

  const updateOnlyPhases =
    dimensions.margin.left ===
      timelineViewRenderingData?.timelineChartRenderingData
        .timelineChartProperties.dimensions.margin.left && !isDataReset;

  const { viewModelData } = timelineViewModelData;

  if (!viewModelData.length) {
    return {
      ...DEFAULT_TIMELINE_CHART_AND_ITEMS_RENDERING_DATA,
      timelineChartRenderingData,
    };
  }

  const allItemsWithRenderingData = {
    allItemLabelsWithRenderingData: updateOnlyPhases
      ? timelineViewRenderingData.allItemsWithRenderingData
          .allItemLabelsWithRenderingData
      : getLabelsWithRenderingParameters({
          items: viewModelData,
          labelsColumnWidth: dimensions.margin.left,
          viewRelevantComponentsScopeData,
        }),

    allItemPhasesWithRenderingData: getPhasesWithRenderingParameters({
      items: viewModelData,
      scaleX: phasesScaler,
      visibleDomain,
      viewRelevantComponentsScopeData,
      isModernizedStyle,
    }),
  };

  const renderedLabelsByGraphNodeId: Map<
    string,
    TimelineLabelWithRenderingParameters
  > = new Map(
    allItemsWithRenderingData.allItemLabelsWithRenderingData.map(item => [
      item.itemModel.id,
      item,
    ])
  );

  return {
    allItemsWithRenderingData,
    timelineChartRenderingData,
    renderedLabelsByGraphNodeId,
    ...getFilteredRenderingDataWithInteractiveNodeStates({
      timelineViewModelData,
      hoveredItemId,
      focusedItemId,
      timelineViewRenderingData: {
        collapsedGroupIds,
        collapsedFieldDataIds,
        allItemsWithRenderingData,
        timelineChartRenderingData,
        backgroundScrollHeight,
        virtualScrollState,
      },
    }),
  };
};
